/**
 * QUIL — live sync monitor (in-memory, never persisted).
 *
 * A single session-level view of OneDrive sync health, fed by the sync engine so
 * the top-bar pill can show a quiet, honest status on every screen without
 * touching Dexie or re-rendering the app on every edit. Distinct from the
 * per-work `getWorkSyncState` (which derives a *persisted* per-work status from
 * the queue) — this is the transient engine-level signal: are we saving right
 * now, are writes failing, have pulls gone stale, are we offline.
 *
 * The engine calls `logSync(kind, ok, note)` on every completed operation and
 * `setSyncActive()` around an in-flight upload drain. Feature B extends
 * `logSync` to also append to a ring buffer for the Settings sync log.
 */

import { useSyncExternalStore } from 'react'

export type SyncEventKind = 'load' | 'pull' | 'write'

/** What the pill actually shows. `healthy` renders nothing. */
export type SyncDisplay = 'healthy' | 'saving' | 'offline' | 'error' | 'stale'

interface MonitorState {
  /** an upload drain is in flight */
  active: boolean
  /** the last online write failed (or the queue is parked) — retryable */
  writeError: boolean
  /** pulls are persistently failing while writes are fine — data may be stale */
  pullStale: boolean
  /** the browser reports no network */
  offline: boolean
}

const initialOffline = typeof navigator !== 'undefined' && navigator.onLine === false

let state: MonitorState = {
  active: false,
  writeError: false,
  pullStale: false,
  offline: initialOffline,
}

// Consecutive online pull failures; two in a row ⇒ we call sync stale rather than
// flapping on a single transient hiccup.
let pullFailStreak = 0
const STALE_AFTER = 2

const listeners = new Set<() => void>()

export function subscribeSyncMonitor(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

export function getSyncMonitorState(): MonitorState {
  return state
}

function setState(patch: Partial<MonitorState>): void {
  const next = { ...state, ...patch }
  if (
    next.active === state.active &&
    next.writeError === state.writeError &&
    next.pullStale === state.pullStale &&
    next.offline === state.offline
  ) return
  state = next
  for (const l of listeners) l()
}

const isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false

/** Test-only: restore the monitor to its initial state between cases.
 * @internal Test seam: resets module state between tests. Never imported by the app.
 */
export function resetSyncMonitor(): void {
  pullFailStreak = 0
  state = { active: false, writeError: false, pullStale: false, offline: false }
  for (const l of listeners) l()
}

/** Called by the engine at the start/end of an upload drain (drives "Saving"). */
export function setSyncActive(active: boolean): void {
  setState({ active })
}

/**
 * Called by the engine on every completed sync operation. Updates the live
 * status; feature B also records it to the ring buffer. Failures only count
 * against health while online — an offline failure is just "Offline", not an
 * error the user should act on.
 */
export function logSync(kind: SyncEventKind, ok: boolean, note?: string): void {
  recordLogEntry(kind, ok, note)
  const online = isOnline()
  switch (kind) {
    case 'write':
      if (ok) setState({ writeError: false })
      else if (online) setState({ writeError: true })
      break
    case 'pull':
    case 'load':
      if (ok) { pullFailStreak = 0; setState({ pullStale: false }) }
      else if (online) {
        pullFailStreak += 1
        if (pullFailStreak >= STALE_AFTER) setState({ pullStale: true })
      }
      break
  }
}

/** Wired to the browser's online/offline events (see `initSyncMonitor`).
 * @internal Exported for its unit tests; production reaches it only from inside this module.
 */
export function setOffline(offline: boolean): void {
  setState({ offline })
}

/**
 * Attach the online/offline listeners once. Returns a teardown. Kept separate
 * from module load so tests can drive `setOffline` directly without real events.
 */
export function initSyncMonitor(): () => void {
  if (typeof window === 'undefined') return () => {}
  const on = () => setOffline(false)
  const off = () => setOffline(true)
  window.addEventListener('online', on)
  window.addEventListener('offline', off)
  setOffline(!isOnline() ? true : false)
  return () => {
    window.removeEventListener('online', on)
    window.removeEventListener('offline', off)
  }
}

/**
 * Precedence for the pill (most actionable first): offline is the honest cause
 * when there's no network, then a real write failure, then in-flight saving,
 * then stale reads. Pure — unit-tested independently of rendering.
 */
export function deriveDisplay(s: MonitorState): SyncDisplay {
  if (s.offline) return 'offline'
  if (s.writeError) return 'error'
  if (s.active) return 'saving'
  if (s.pullStale) return 'stale'
  return 'healthy'
}

/** Subscribe a component to the derived display; re-renders only when it changes. */
export function useSyncDisplay(): SyncDisplay {
  const get = () => deriveDisplay(state)
  return useSyncExternalStore(subscribeSyncMonitor, get, get)
}

// ─── Feature B (sync log) hook — filled in by syncLog.ts registering a sink ─────
// Kept as an injectable sink so feature A carries no ring-buffer dependency; the
// log module registers itself. A no-op until then.
type LogSink = (kind: SyncEventKind, ok: boolean, note: string | undefined, at: number) => void
let logSink: LogSink | null = null
let clock: () => number = () => 0

export function registerLogSink(sink: LogSink, now: () => number): void {
  logSink = sink
  clock = now
}

function recordLogEntry(kind: SyncEventKind, ok: boolean, note?: string): void {
  logSink?.(kind, ok, note, clock())
}
